import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import {
  Sparkles,
  TrendingUp,
  AlertTriangle,
  CircleDot,
  RefreshCw,
  Wallet,
  Building2,
  BarChart3,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const verdictConfig = {
  firsat: {
    label: "FIRSAT",
    color: "#10B981",
    bg: "bg-emerald-50 dark:bg-emerald-900/20",
    text: "text-emerald-700 dark:text-emerald-300",
    ring: "ring-emerald-200 dark:ring-emerald-800",
    icon: TrendingUp,
  },
  orta: {
    label: "ORTA",
    color: "#3B82F6",
    bg: "bg-blue-50 dark:bg-blue-900/20",
    text: "text-blue-700 dark:text-blue-300",
    ring: "ring-blue-200 dark:ring-blue-800",
    icon: CircleDot,
  },
  riskli: {
    label: "RİSKLİ",
    color: "#EF4444",
    bg: "bg-red-50 dark:bg-red-900/20",
    text: "text-red-700 dark:text-red-300",
    ring: "ring-red-200 dark:ring-red-800",
    icon: AlertTriangle,
  },
  yetersiz_veri: {
    label: "VERİ AZ",
    color: "#6B7280",
    bg: "bg-gray-50 dark:bg-gray-800",
    text: "text-gray-700 dark:text-gray-300",
    ring: "ring-gray-200 dark:ring-gray-700",
    icon: CircleDot,
  },
};

const AIPortfolioDashboard = ({ onPropertyClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  const fetchSummary = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/ai/portfolio-summary`, {
        withCredentials: true,
      });
      setData(data);
    } catch (error) {
      console.error("Portfolio summary error:", error);
      toast.error("Portföy özeti yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  const handleAnalyzeAll = async () => {
    setAnalyzing(true);
    try {
      const { data: result } = await axios.post(
        `${API_URL}/api/ai/analyze-all`,
        {},
        { withCredentials: true }
      );
      if (result.error) {
        toast.error(result.error);
      } else {
        toast.success(
          `${result.analyzed} ilan analiz edildi${
            result.failed > 0 ? `, ${result.failed} başarısız` : ""
          }`
        );
      }
      await fetchSummary();
    } catch (error) {
      console.error("Analyze all error:", error);
      toast.error("Toplu analiz başarısız");
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-[#52525B] dark:text-[#A1A1AA]">Yükleniyor...</div>
      </div>
    );
  }

  if (!data || data.total_properties === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <Sparkles className="w-12 h-12 text-[#F59E0B] mb-4" />
        <h2 className="text-xl font-bold text-[#0A0A0A] dark:text-[#FAFAFA] mb-2">
          Henüz portföy verisi yok
        </h2>
        <p className="text-sm text-[#52525B] dark:text-[#A1A1AA] text-center max-w-sm">
          AI Panelim, portföyünüzdeki ilanların analizini burada gösterecek. Önce
          birkaç gayrimenkul ekleyin.
        </p>
      </div>
    );
  }

  const totalVerdicts = Object.values(data.verdict_distribution).reduce(
    (a, b) => a + b,
    0
  );

  return (
    <div
      data-testid="ai-portfolio-dashboard"
      className="p-6 max-w-7xl mx-auto space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#D97706] flex items-center justify-center shadow-md">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A] dark:text-[#FAFAFA]">
              AI Panelim
            </h1>
            <span className="text-[10px] uppercase tracking-widest bg-[#F59E0B] text-white px-2 py-0.5 rounded-full font-bold">
              GPT-5.2
            </span>
          </div>
          <p className="text-sm text-[#52525B] dark:text-[#A1A1AA] ml-13 pl-1">
            Portföyünüzün yapay zeka destekli özet analizi
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            data-testid="refresh-portfolio-button"
            onClick={fetchSummary}
            variant="outline"
            size="sm"
            className="border-[#E4E4E7] dark:border-[#27272A]"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Yenile
          </Button>
          {data.unanalyzed_count > 0 && (
            <Button
              data-testid="analyze-all-button"
              onClick={handleAnalyzeAll}
              disabled={analyzing}
              size="sm"
              className="bg-gradient-to-r from-[#F59E0B] to-[#D97706] hover:from-[#D97706] hover:to-[#B45309] text-white"
            >
              <Sparkles
                className={`w-4 h-4 mr-2 ${analyzing ? "animate-pulse" : ""}`}
              />
              {analyzing
                ? "Analiz Ediliyor..."
                : `${data.unanalyzed_count} İlanı Analiz Et`}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Building2}
          label="Toplam İlan"
          value={data.total_properties}
          testId="stat-total-properties"
          accent="#0A0A0A"
        />
        <StatCard
          icon={Wallet}
          label="Toplam Değer"
          value={`${(data.total_value / 1_000_000).toLocaleString("tr-TR", {
            maximumFractionDigits: 1,
          })}M TL`}
          testId="stat-total-value"
          accent="#F59E0B"
        />
        <StatCard
          icon={BarChart3}
          label="Ortalama m² Fiyatı"
          value={
            data.avg_price_per_m2
              ? `${data.avg_price_per_m2.toLocaleString("tr-TR")} TL`
              : "—"
          }
          testId="stat-avg-price-m2"
          accent="#3B82F6"
        />
        <StatCard
          icon={Sparkles}
          label="Analiz Edilmiş"
          value={`${data.analyzed_count} / ${data.total_properties}`}
          testId="stat-analyzed-count"
          accent="#10B981"
        />
      </div>

      {/* Verdict Distribution */}
      <div className="bg-white dark:bg-[#18181B] border border-[#E4E4E7] dark:border-[#27272A] rounded-lg p-6">
        <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-[#52525B] dark:text-[#A1A1AA] mb-4">
          AI Değerlendirme Dağılımı
        </h2>

        {totalVerdicts === 0 ? (
          <div className="text-sm text-[#52525B] dark:text-[#A1A1AA] py-6 text-center">
            Henüz analiz edilmiş ilan yok. Yukarıdan "Analiz Et" ile başlayın.
          </div>
        ) : (
          <>
            <div className="flex h-12 rounded-lg overflow-hidden mb-4">
              {Object.entries(data.verdict_distribution).map(([key, count]) => {
                if (count === 0) return null;
                const percent = (count / totalVerdicts) * 100;
                const cfg = verdictConfig[key];
                return (
                  <div
                    key={key}
                    style={{
                      width: `${percent}%`,
                      backgroundColor: cfg.color,
                    }}
                    className="flex items-center justify-center text-white text-xs font-bold transition-all duration-300 hover:brightness-110"
                    title={`${cfg.label}: ${count} ilan (${percent.toFixed(0)}%)`}
                  >
                    {percent > 8 && `${percent.toFixed(0)}%`}
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(data.verdict_distribution).map(([key, count]) => {
                const cfg = verdictConfig[key];
                const Icon = cfg.icon;
                return (
                  <div
                    key={key}
                    data-testid={`verdict-${key}`}
                    className={`flex items-center gap-3 p-3 rounded-lg ${cfg.bg} ring-1 ${cfg.ring}`}
                  >
                    <Icon className={`w-5 h-5 ${cfg.text}`} />
                    <div>
                      <div className={`text-xs font-bold ${cfg.text}`}>
                        {cfg.label}
                      </div>
                      <div className="text-lg font-bold text-[#0A0A0A] dark:text-[#FAFAFA]">
                        {count}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Top Opportunities */}
      <div>
        <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-[#52525B] dark:text-[#A1A1AA] mb-4">
          AI'nın Önerdiği En Güçlü 3 Fırsat
        </h2>

        {data.top_opportunities.length === 0 ? (
          <div className="bg-white dark:bg-[#18181B] border border-[#E4E4E7] dark:border-[#27272A] rounded-lg p-8 text-center">
            <p className="text-sm text-[#52525B] dark:text-[#A1A1AA]">
              Fırsat listesi için önce ilanlarınızı analiz edin.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.top_opportunities.map((prop, idx) => {
              const cfg =
                verdictConfig[prop.ai_summary?.verdict] || verdictConfig.orta;
              return (
                <div
                  key={prop.id}
                  data-testid={`top-opportunity-${idx}`}
                  onClick={() => onPropertyClick(prop)}
                  className="group bg-white dark:bg-[#18181B] border border-[#E4E4E7] dark:border-[#27272A] rounded-lg overflow-hidden cursor-pointer hover:-translate-y-1 hover:shadow-lg transition-transform duration-200"
                >
                  {prop.image_url && (
                    <div className="h-32 overflow-hidden relative">
                      <img
                        src={prop.image_url}
                        alt={prop.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 left-2 bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest">
                        #{idx + 1} Fırsat
                      </div>
                    </div>
                  )}
                  <div className="p-4">
                    <div
                      className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${cfg.bg} ${cfg.text} mb-2`}
                    >
                      {cfg.label}
                    </div>
                    <h3 className="font-bold text-[#0A0A0A] dark:text-[#FAFAFA] mb-1">
                      {prop.title}
                    </h3>
                    <p className="text-xs text-[#52525B] dark:text-[#A1A1AA] mb-3">
                      {prop.location}
                    </p>
                    <div className="flex items-baseline justify-between mb-3">
                      <span className="text-lg font-bold text-[#F59E0B]">
                        {prop.price.toLocaleString("tr-TR")} TL
                      </span>
                      {prop.area && (
                        <span className="text-xs text-[#52525B] dark:text-[#A1A1AA]">
                          {Math.round(prop.price / prop.area).toLocaleString(
                            "tr-TR"
                          )}{" "}
                          TL/m²
                        </span>
                      )}
                    </div>
                    {prop.ai_summary && (
                      <div className="pt-3 border-t border-[#E4E4E7] dark:border-[#27272A]">
                        <p className="text-xs font-semibold text-[#0A0A0A] dark:text-[#FAFAFA] mb-1">
                          {prop.ai_summary.headline}
                        </p>
                        <p className="text-xs text-[#52525B] dark:text-[#A1A1AA] leading-relaxed line-clamp-3">
                          {prop.ai_summary.summary}
                        </p>
                      </div>
                    )}
                    <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#F59E0B] group-hover:gap-2 transition-all duration-200">
                      Detayları Gör <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, testId, accent }) => (
  <div
    data-testid={testId}
    className="bg-white dark:bg-[#18181B] border border-[#E4E4E7] dark:border-[#27272A] rounded-lg p-4"
  >
    <div className="flex items-center gap-3 mb-2">
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center"
        style={{ backgroundColor: `${accent}15` }}
      >
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#52525B] dark:text-[#A1A1AA]">
        {label}
      </span>
    </div>
    <div className="text-2xl font-bold text-[#0A0A0A] dark:text-[#FAFAFA] tracking-tight">
      {value}
    </div>
  </div>
);

export default AIPortfolioDashboard;
